import { IconType } from 'react-icons';

// ---------------------------------------------------------------------------
// Extra icon libraries from react-icons, addressed as `<alias>-<Name>`, e.g.
// `phosphor-HouseBold` → `PiHouseBold` from `react-icons/pi`. Bare PascalCase
// names are Phosphor duotone (see `duotone.ts`). Each library is its own lazy
// chunk (Phosphor is ~5 MB), downloaded only once an icon from it is rendered
// or browsed.
//
// Kept to a deliberate few: every library added here is parsed by the
// production build, and all of them together exhaust its memory budget.
// Aliases must never be `fa`: `fa-…` strings are Font Awesome class names.
// `fa6` is retired too: Icon renders saved `fa6-…` names as FA 5 classes.
// ---------------------------------------------------------------------------

export type IconModule = Record<string, IconType>;

export interface ReactIconPack {
  alias: string;
  title: string;
  /** Prefix react-icons puts on every export of this library, e.g. `Pi`. */
  prefix: string;
  load: () => Promise<IconModule>;
}

const lazy = (load: () => Promise<unknown>) => load as () => Promise<IconModule>;

export const REACT_ICON_PACKS: ReactIconPack[] = [
  { alias: 'phosphor', title: 'Phosphor', prefix: 'Pi', load: lazy(() => import('virtual:phosphor-pack')) },
];

const PACKS_BY_ALIAS = new Map(REACT_ICON_PACKS.map(pack => [pack.alias, pack]));

// Names may start with a digit (`Fa500Px`), so the name part allows one.
const PREFIXED_NAME = /^([a-z][a-z0-9]*)-([A-Z0-9][A-Za-z0-9]*)$/;

export interface ReactIconRef {
  pack: ReactIconPack;
  exportName: string;
}

/** Resolves `phosphor-HouseBold` to its library + export; null for anything else. */
export function parseReactIconName(icon: string | null | undefined): ReactIconRef | null {
  const match = icon ? PREFIXED_NAME.exec(icon) : null;
  const pack = match ? PACKS_BY_ALIAS.get(match[1]) : undefined;
  if (!match || !pack) return null;

  return { pack, exportName: pack.prefix + match[2] };
}

const loadedPacks = new Map<string, IconModule>();
const pendingPacks = new Map<string, Promise<IconModule>>();

export function getLoadedPack(pack: ReactIconPack): IconModule | undefined {
  return loadedPacks.get(pack.alias);
}

export function loadPack(pack: ReactIconPack): Promise<IconModule> {
  const loaded = loadedPacks.get(pack.alias);
  if (loaded) return Promise.resolve(loaded);

  let pending = pendingPacks.get(pack.alias);

  if (!pending) {
    pending = pack
      .load()
      .then(module => {
        loadedPacks.set(pack.alias, module);

        return module;
      })
      .finally(() => pendingPacks.delete(pack.alias));
    pendingPacks.set(pack.alias, pending);
  }

  return pending;
}

/** Every icon in a library, as picker items in the stored `{ icon }` format. */
export function listPackIcons(pack: ReactIconPack): Promise<{ icon: string }[]> {
  return loadPack(pack).then(module =>
    Object.keys(module)
      .filter(name => name.startsWith(pack.prefix))
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ icon: `${pack.alias}-${name.slice(pack.prefix.length)}` }))
  );
}
